import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { BackendService } from './backend.service';
import {
	NumberOfBranchesResponse,
	CallingTree,
	CallingTreeBranchResponse,
	CallingTreeMembersResponse,
} from 'src/shared/models/calling-tree.model';
import {
	CallingTreeAdminsResponse,
	CallingTreeAdmins,
} from 'src/shared/models/calling-tree-admins.model';
import {
	CallingTreeBranchViewResponse,
	CallingTreeView,
} from 'src/shared/models/calling-tree-view.model';
import {
	UserDropDown,
	UserDropDownResponse,
} from 'src/shared/models/user-drop-down.model';
import { CallingTreeMember } from 'src/shared/models/calling-tree-member.model';

const API_URL = `${environment.apiUrl}/web/callingtree`;

export const NUMBER_OF_BRANCHES_URL = `${API_URL}/numberofbranches`;
export const NUMBER_OF_BRANCHES_RESPONSE = 'Fetched Number of Branches';

export const SELECT_CALLING_TREE_URL = `${API_URL}/branch`;
export const SELECT_CALLING_TREE_RESPONSE = 'Fetched Calling Tree Branch';

export const UPDATE_COORDINATORS_URL = `${API_URL}/updatecoordinators`;
export const UPDATE_COORDINATORS_RESPONSE = 'Updated Calling Tree Coordinators';

export const ADMINS_URL = `${API_URL}/admins`;
export const ADMINS_RESPONSE = 'Fetched Calling Tree Administrators';

export const UPDATE_ADMINS_URL = `${API_URL}/updateadmins`;
export const UPDATE_ADMINS_RESPONSE = 'Updated Calling Tree Administrators';

export const VIEW_URL = `${API_URL}/view`;
export const VIEW_RESPONSE = 'Fetched Calling Tree View';

export const AVAILABLE_MEMBERS_URL = `${API_URL}/availablemembers`;
export const AVAILABLE_MEMBERS_RESPONSE = 'Fetched Available Members';

export const ADD_USER_TO_BRANCH_URL = `${API_URL}/addusertobranch`;
export const ADD_USER_TO_BRANCH_RESPONSE = 'Added User to Calling Tree Branch';

export const REMOVE_USER_FROM_BRANCH_URL = `${API_URL}/removeuserfrombranch`;
export const REMOVE_USER_FROM_BRANCH_RESPONSE =
	'Removed User from Calling Tree Branch';

@Injectable({
	providedIn: 'root',
})
export class CallingTreeBackendService {
	private backend = inject(BackendService);

	/**
	 * Get Number of Branches
	 * =======================
	 * API Endpoint: /web/callingtree/numberofbranches
	 *
	 * Returns the number of available calling tree branches
	 */
	numberOfBranches(): Observable<number> {
		return this.backend
			.post(NUMBER_OF_BRANCHES_URL, NUMBER_OF_BRANCHES_RESPONSE, {})
			.pipe(
				map(
					(response: NumberOfBranchesResponse) =>
						response.numberOfBranches
				)
			);
	}

	/**
	 * Select Calling Tree Branch
	 * ===========================
	 * API Endpoint: /web/callingtree/branch
	 * JSON Parameters: branch: number
	 *
	 * Returns the selected Calling Tree branch
	 */
	selectCallingTreeBranch(branch: number): Observable<CallingTree> {
		return this.backend
			.post(SELECT_CALLING_TREE_URL, SELECT_CALLING_TREE_RESPONSE, {
				branch: branch,
			})
			.pipe(
				map((response: CallingTreeBranchResponse) =>
					CallingTree.fromJSON(response.callingTree)
				)
			);
	}

	/**
	 * Update Calling Tree Coordinators
	 * =================================
	 * API Endpoint: /web/callingtree/updatecoordinators
	 * JSON Parameters: branch, activeCoordinatorId, backupCoordinatorId
	 *
	 * Updates the selected coordinators and returns the updated Calling Tree object
	 */
	updateCoordinators(callingTree: CallingTree): Observable<CallingTree> {
		return this.backend
			.post(UPDATE_COORDINATORS_URL, UPDATE_COORDINATORS_RESPONSE, {
				branch: callingTree.branch,
				activeCoordinatorId: callingTree.activeCoordinatorId,
				backupCoordinatorId: callingTree.backupCoordinatorId,
			})
			.pipe(
				map((response: CallingTreeBranchResponse) =>
					CallingTree.fromJSON(response.callingTree)
				)
			);
	}

	/**
	 * Get Calling Tree Admins
	 * ========================
	 * API Endpoint: /web/callingtree/admins
	 *
	 * Selects the Calling Tree Administrators
	 */
	getAdmins(): Observable<CallingTreeAdminsResponse> {
		return this.backend
			.post(ADMINS_URL, ADMINS_RESPONSE, {})
			.pipe(map((response: CallingTreeAdminsResponse) => response));
	}

	/**
	 * Update Calling Tree Administrators
	 * ===================================
	 * API Endpoint: /web/callingtree/updateadmins
	 * JSON Parameters:
	 * 		root: number
	 * 		manager: number
	 * 		superCoordinators: CallingTreeSuperCoordinator[]
	 *
	 * Updates the selected administrators and returns the updated objects
	 */
	updateAdmins(
		admins: CallingTreeAdmins
	): Observable<CallingTreeAdminsResponse> {
		return this.backend
			.post(UPDATE_ADMINS_URL, UPDATE_ADMINS_RESPONSE, {
				root: admins.rootUserId,
				manager: admins.managerUserId,
				superCoordinators: admins.superCoordinators,
			})
			.pipe(map((response: CallingTreeAdminsResponse) => response));
	}

	/**
	 * Get Calling Tree View
	 * ======================
	 * API Endpoint: /web/callingtree/view
	 *
	 * Selects the Calling Tree View
	 */
	getView(): Observable<CallingTreeView> {
		return this.backend
			.post(VIEW_URL, VIEW_RESPONSE, {})
			.pipe(
				map((response: CallingTreeBranchViewResponse) =>
					CallingTreeView.fromJSON(response.view)
				)
			);
	}

	/**
	 * Available Branch Members
	 * =========================
	 * API Endpoint: /web/callingtree/availablemembers
	 * JSON Parameters:
	 * 		branch: number
	 *
	 * Selects any available (enabled and not already in the branch) Users
	 */
	getAvailableBranchMembers(branch: number): Observable<UserDropDown[]> {
		return this.backend
			.post(AVAILABLE_MEMBERS_URL, AVAILABLE_MEMBERS_RESPONSE, {
				branch: branch,
			})
			.pipe(
				map((response: UserDropDownResponse) =>
					response.dropdowns.map((json) =>
						UserDropDown.fromJSON(json)
					)
				)
			);
	}

	/**
	 * Add User to Calling Tree Branch
	 * ================================
	 * API Endpoint: /web/callingtree/addusertobranch
	 * JSON Parameters:
	 * 		userId: number
	 * 		branch: number
	 *
	 * Adds the provided user to the provided branch, and then returns that branch's member list
	 */
	addUserToBranch(
		userId: number,
		branch: number
	): Observable<CallingTreeMember[]> {
		return this.backend
			.post(ADD_USER_TO_BRANCH_URL, ADD_USER_TO_BRANCH_RESPONSE, {
				userId: userId,
				branch: branch,
			})
			.pipe(
				map((response: CallingTreeMembersResponse) =>
					response.members.map((json) =>
						CallingTreeMember.fromJSON(json)
					)
				)
			);
	}

	/**
	 * Remove User From Calling Tree Branch
	 * =====================================
	 * API Endpoint: /web/callingtree/removeuserfrombranch
	 * JSON Parameters:
	 * 		userId: number
	 * 		newBranch: number (optional re-assignment branch)
	 *
	 * Removes the provided user from their current branch (and optionally re-assigns them to a new branch),
	 * and then returns the old branch's member list.
	 */
	removeUserFromBranch(
		userId: number,
		newBranch: number
	): Observable<CallingTreeMember[]> {
		return this.backend
			.post(
				REMOVE_USER_FROM_BRANCH_URL,
				REMOVE_USER_FROM_BRANCH_RESPONSE,
				{
					userId: userId,
					branch: newBranch,
				}
			)
			.pipe(
				map((response: CallingTreeMembersResponse) =>
					response.members.map((json) =>
						CallingTreeMember.fromJSON(json)
					)
				)
			);
	}
}
